import type { ArchivedAnswer } from './submissions';

/** 'survey' is ungraded: no scoring, no results[], thank-you screen at the end. */
export type QuizType = 'personality' | 'scored' | 'outcome' | 'assessment' | 'survey';

export interface QuizAnswer {
  id: string;
  text: string;
  imageUrl?: string;
  score: number;
  outcomeId?: string;
  isCorrect?: boolean; // assessment quizzes: marks the correct answer(s)
  /** Video quizzes: what plays after this answer (default: continue the main video). */
  next?: VideoAnswerNext;
  /** Video quizzes: ClickFunnels tag ids applied when this answer is chosen. */
  tagIds?: string[];
}

export type VideoAnswerNext =
  | { type: 'continue' }
  | { type: 'clip'; segmentId: string }
  /** Ask a follow-up question (target must exist and have `followUp: true`). */
  | { type: 'ask'; questionId: string };

export interface QuizQuestion {
  id: string;
  type: 'single_choice' | 'multi_choice' | 'image_choice' | 'yes_no' | 'text_input';
  title: string;
  description?: string;
  imageUrl?: string;
  required: boolean;
  sortOrder: number;
  answers: QuizAnswer[];
  /** Video quizzes: seconds into the main video where it stops (unique, 0 < at < mainEnd). */
  at?: number;
  /** Video quizzes: ClickFunnels custom attribute that receives the answer text. */
  attributeKey?: string;
  /** Video quizzes: a follow-up has no `at`; it's only shown when an answer's `next` asks it. */
  followUp?: true;
}

/** Question types a video quiz supports. */
export const VIDEO_QUESTION_TYPES = ['single_choice', 'multi_choice', 'yes_no', 'text_input'] as const;

/** A branch clip stored after `mainEnd` in the same video file. */
export interface VideoSegment {
  id: string;
  label: string;
  start: number;
  end: number;
}

/** Timed caption over the main video; text may use {{first_name|fallback}} tokens. */
export interface VideoCaption {
  id: string;
  from: number;
  to: number;
  text: string;
}

export type VideoDriver = 'native' | 'voomly';

/** `quizzes.video` — null/absent means a classic quiz. */
export interface VideoConfig {
  driver: VideoDriver;
  /** Required when driver = 'voomly' (the data-id from Voomly's embed code). */
  voomlyId?: string;
  /** ≤ 100, from < to, text ≤ 200 chars. */
  captions?: VideoCaption[];
  /** Show the fullscreen button. Default true. */
  allowFullscreen?: boolean;
  /** R2 key in MEDIA (preferred). */
  mediaKey?: string;
  /** OR an https URL to an MP4/WebM. */
  url?: string;
  posterKey?: string;
  /** Seconds: where the main video ends; branch clips live after it. */
  mainEnd: number;
  /** Seconds, informational. */
  duration?: number;
  segments: VideoSegment[];
  enforcement: { preventSkip: boolean };
  leadTiming: 'start' | 'end';
  incompleteTagIds?: string[];
}

export type VideoStyleProp =
  | 'color' | 'background' | 'backgroundColor' | 'borderColor' | 'borderWidth' | 'borderStyle'
  | 'borderRadius' | 'fontFamily' | 'fontSize' | 'fontWeight' | 'padding' | 'margin' | 'gap' | 'boxShadow'
  | 'textAlign' | 'width' | 'maxWidth' | 'height' | 'opacity';

export type VideoPart =
  | 'root' | 'player' | 'video' | 'play-button' | 'progress' | 'progress-bar'
  | 'overlay' | 'question-card' | 'question-title' | 'question-description'
  | 'answers' | 'answer-button' | 'answer-button-selected' | 'checkbox' | 'text-answer'
  | 'continue-button' | 'lead-form' | 'lead-label' | 'lead-input' | 'lead-button' | 'lead-error'
  | 'resume-card' | 'resume-button' | 'end-card' | 'end-title' | 'end-text' | 'end-button' | 'error'
  | 'caption' | 'fullscreen-button';

export interface VideoStyleTheme {
  fontFamily: string;
  primaryColor: string;
  textColor: string;
  backgroundColor: string;
  cardBackground: string;
  borderRadius: string;
  spacing: string;
  maxWidth: string;
}

/** `styleConfig.video` */
export interface VideoStyle {
  theme: VideoStyleTheme;
  components: Partial<Record<VideoPart, Partial<Record<VideoStyleProp, string>>>>;
  /** ≤ 20 KB, scoped into the player's shadow root. */
  customCss?: string;
}

/** GET /api/quizzes/:id/video/stats */
export interface VideoQuizDaily {
  day: string;
  views: number;
  starts: number;
  completions: number;
  synced: number;
  failed: number;
}

export interface VideoQuizFailedSession {
  id: string;
  email: string | null;
  attempts: number;
  lastError: string | null;
  updatedAt: number;
}

export interface VideoQuizStats {
  daily: VideoQuizDaily[];
  checkpoints: Array<{ questionId: string; reached: number }>;
  pending: number;
  failed: VideoQuizFailedSession[];
}

export interface QuizResult {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  ctaText: string;
  ctaUrl: string;
  redirectOnResult?: boolean;
  minScore?: number;
  maxScore?: number;
  tagId?: string; // legacy single per-result tag (still honored on submit)
  tagIds?: string[]; // per-result tags applied when this result wins
  customAttributes: Record<string, string>;
}

export interface QuizLeadCapture {
  enabled: boolean;
  position: 'before_results' | 'first_question';
  fields: { email: boolean; firstName: boolean; lastName: boolean; phone: boolean };
  headline: string;
  subtext: string;
}

export interface QuizStyleConfig {
  primaryColor: string;
  backgroundColor: string;
  textColor: string;
  fontFamily: string;
  buttonText: string;
  borderRadius: string;
  quizWidth: string;
  showProgressBar: boolean;
  showQuestionNumbers: boolean;
  inputBackgroundColor: string;
  inputBorderColor: string;
  backgroundOpacity: number;
  /** Video quizzes only. */
  video?: VideoStyle;
}

/** Survey end screen. Ignored by every non-survey quiz type. */
export interface QuizThankYou {
  headline?: string;
  message?: string;
  ctaText?: string;
  ctaUrl?: string;
  /** When set, the taker is redirected here instead of seeing the screen. */
  redirectUrl?: string;
}

export interface QuizSettings {
  tagIds: string[];
  resultAttributeKey: string;
  scoreAttributeKey: string;
  showCorrectAnswers: boolean;
  randomizeQuestions: boolean;
  randomizeAnswers: boolean;
  thankYou?: QuizThankYou;
}

export interface Quiz {
  id: string;
  name: string;
  slug: string;
  connectionId: string;
  /** List responses only: true when the quiz has a video config. */
  isVideo?: boolean;
  connectionName?: string;
  status: 'draft' | 'published' | 'archived';
  quizType: QuizType;
  questions: QuizQuestion[];
  results: QuizResult[];
  leadCapture: QuizLeadCapture;
  styleConfig: QuizStyleConfig;
  settings: QuizSettings;
  /** Null/absent = classic quiz. */
  video?: VideoConfig | null;
  submissionCount?: number;
  createdAt: number;
  updatedAt: number;
}

export interface QuizSubmission {
  id: string;
  quizId: string;
  data: Record<string, string>;
  resultId: string | null;
  resultTitle?: string | null;
  score: number | null;
  cfContactId: string | null;
  status: string;
  errorMessage: string | null;
  /** False for rows written before answers were archived to R2. */
  hasAnswers?: boolean;
  /** Populated only when the list is fetched with `full=1`. */
  answers?: ArchivedAnswer[] | null;
  createdAt: number;
}
